import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Bell,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  ExternalLink,
  GripVertical,
  ListChecks,
  Menu,
  PackageCheck,
  Settings,
  Store,
  Truck,
  Users,
  UtensilsCrossed,
  X,
} from "lucide-react";

const PRIMARY = [
  { id: "storefront", to: "/vendor/storefront", label: "Storefront", icon: Store, testid: "seller-menu-storefront" },
  { id: "orders", to: "/vendor/orders", label: "Orders", icon: ClipboardList, testid: "seller-menu-orders" },
  { id: "menu", to: "/vendor/menu", label: "Menu", icon: UtensilsCrossed, testid: "seller-menu-menu" },
];

const CUSTOM_TOOLS = [
  { id: "production", to: "/vendor/production", label: "Prep Board", icon: PackageCheck, testid: "seller-menu-production" },
  { id: "delivery", to: "/vendor/delivery", label: "Fulfillment", icon: Truck, testid: "seller-menu-delivery" },
  { id: "growth", to: "/vendor/growth", label: "Growth", icon: Users, testid: "seller-menu-growth" },
  { id: "drops", to: "/vendor/drops", label: "Preorder Drops", icon: CalendarClock, testid: "seller-menu-drops" },
];

const ACCOUNT = [
  { to: "/vendor/account", label: "Profile & Settings", icon: Settings, testid: "seller-menu-settings" },
];

const VALID_PLACEMENTS = new Set(["storefront", "orders", "menu", "more"]);
const DEFAULT_ORDER = CUSTOM_TOOLS.map((item) => item.id);

function defaultPreferences() {
  return {
    order: [...DEFAULT_ORDER],
    placements: Object.fromEntries(DEFAULT_ORDER.map((id) => [id, "more"])),
  };
}

function normalizePreferences(value) {
  const incomingOrder = Array.isArray(value?.order) ? value.order.filter((id) => DEFAULT_ORDER.includes(id)) : [];
  const order = [...incomingOrder, ...DEFAULT_ORDER.filter((id) => !incomingOrder.includes(id))];
  const placements = {};
  for (const id of DEFAULT_ORDER) {
    const proposed = value?.placements?.[id];
    placements[id] = VALID_PLACEMENTS.has(proposed) ? proposed : "more";
  }
  return { order, placements };
}

function preferenceKey(vendorId) {
  return `arcticplate:seller-menu:v1:${vendorId || "unknown"}`;
}

export default function SellerWorkspaceMenu({
  vendor,
  notificationUnread = 0,
  showSetup = false,
}) {
  const [open, setOpen] = useState(false);
  const [arranging, setArranging] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const longPressTimer = useRef(null);
  const suppressNextClick = useRef(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setArranging(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!vendor?.id || typeof window === "undefined") {
      setPreferences(defaultPreferences());
      return;
    }
    try {
      const raw = window.localStorage.getItem(preferenceKey(vendor.id));
      setPreferences(normalizePreferences(raw ? JSON.parse(raw) : null));
    } catch {
      setPreferences(defaultPreferences());
    }
  }, [vendor?.id]);

  useEffect(() => {
    if (!vendor?.id || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(preferenceKey(vendor.id), JSON.stringify(preferences));
    } catch {
      // Navigation preference persistence is optional; menu remains usable.
    }
  }, [preferences, vendor?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => () => {
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
  }, []);

  const beginLongPress = () => {
    if (arranging) return;
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = window.setTimeout(() => {
      suppressNextClick.current = true;
      setArranging(true);
    }, 600);
  };

  const endLongPress = () => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const orderedTools = preferences.order
    .map((id) => CUSTOM_TOOLS.find((item) => item.id === id))
    .filter(Boolean);

  const customFor = (placement) => orderedTools
    .filter((item) => preferences.placements[item.id] === placement)
    .map((item) => ({ ...item, customizable: true, subItem: placement !== "more" }));

  const workspaceItems = PRIMARY.flatMap((item) => [
    item,
    ...customFor(item.id),
  ]);
  const moreItems = customFor("more");

  const placeTool = (id, placement) => {
    if (!VALID_PLACEMENTS.has(placement)) return;
    setPreferences((current) => ({
      ...current,
      placements: { ...current.placements, [id]: placement },
    }));
  };

  const moveTool = (id, delta) => {
    setPreferences((current) => {
      const placement = current.placements[id] || "more";
      const group = current.order.filter((toolId) => (current.placements[toolId] || "more") === placement);
      const index = group.indexOf(id);
      const targetIndex = index + delta;
      if (index < 0 || targetIndex < 0 || targetIndex >= group.length) return current;
      const targetId = group[targetIndex];
      const order = [...current.order];
      const from = order.indexOf(id);
      const to = order.indexOf(targetId);
      [order[from], order[to]] = [order[to], order[from]];
      return { ...current, order };
    });
  };

  const resetMenu = () => setPreferences(defaultPreferences());

  return (
    <>
      <div className="sticky top-0 z-[60] flex h-16 items-center justify-between gap-2 border-b border-[#efd3bf] bg-[#fff9f3]/96 px-3 backdrop-blur md:hidden" data-testid="seller-mobile-topbar">
        <div className="flex min-w-0 items-center gap-2.5">
          {vendor?.image_url ? (
            <img src={vendor.image_url} alt="" aria-hidden="true" className="h-10 w-10 shrink-0 rounded-full border border-[#e5b995] bg-white object-cover" />
          ) : (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e5b995] bg-[#fff1e5] text-[var(--ap-primary)]" aria-hidden="true">
              <Store size={18} strokeWidth={1.8} />
            </span>
          )}
          <div className="min-w-0 border-l border-[#e5b995] pl-2.5">
            <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-[var(--ap-primary)]">Kitchen workspace</div>
            <div className="max-w-[120px] truncate font-serif text-base font-semibold text-[var(--ap-ink)]">{vendor?.business_name || "Your kitchen"}</div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Link
            to="/vendor/notifications"
            data-testid="seller-topbar-notifications"
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#e5b995] bg-white text-[var(--ap-ink)] shadow-sm"
            aria-label={Number(notificationUnread || 0) > 0 ? `Notifications, ${notificationUnread} unread` : "Notifications"}
          >
            <Bell size={17} aria-hidden="true" />
            {Number(notificationUnread || 0) > 0 && (
              <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-[var(--ap-primary)] px-1.5 py-0.5 text-center text-[9px] font-bold text-white">
                {Number(notificationUnread) > 99 ? "99+" : Number(notificationUnread)}
              </span>
            )}
          </Link>
          <Link
            to="/vendor/storefront"
            data-testid="seller-topbar-storefront"
            className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-[#e5b995] bg-white px-2.5 text-[11px] font-extrabold text-[var(--ap-ink)] shadow-sm"
            aria-label="Open storefront and business profile"
          >
            <Store size={15} aria-hidden="true" />
            <span className="hidden min-[390px]:inline">Storefront</span>
          </Link>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full bg-[var(--ap-primary)] px-3 text-xs font-extrabold text-white shadow-sm transition hover:bg-[var(--ap-primary-hover-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ap-primary)] focus-visible:ring-offset-2"
            aria-label="Open kitchen workspace menu"
            aria-expanded={open}
            aria-controls="seller-workspace-menu"
            data-testid="seller-workspace-menu-button"
          >
            <Menu size={17} aria-hidden="true" /> Menu
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] md:hidden" data-testid="seller-workspace-menu-shell">
          <button
            type="button"
            className="absolute inset-0 bg-[#17212b]/50 backdrop-blur-[2px]"
            aria-label="Close kitchen menu"
            onClick={() => setOpen(false)}
          />
          <aside
            id="seller-workspace-menu"
            className="absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-[#fff9f3] shadow-[-24px_0_70px_-30px_rgba(112,52,20,0.45)]"
            aria-label="Kitchen workspace navigation"
          >
            <div className="flex items-start justify-between gap-4 border-b border-[#eadfd3] px-5 py-5">
              <div className="flex min-w-0 items-center gap-3">
                {vendor?.image_url ? (
                  <img src={vendor.image_url} alt="" aria-hidden="true" className="h-11 w-11 shrink-0 rounded-full border border-[var(--ap-border)] bg-white object-cover" />
                ) : (
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--ap-border)] bg-[var(--ap-surface)] text-[var(--ap-ink)]" aria-hidden="true">
                    <Store size={19} strokeWidth={1.8} />
                  </span>
                )}
                <div className="min-w-0 border-l border-[#ded6cb] pl-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--ap-primary)]">
                    Kitchen workspace
                  </div>
                  <div className="mt-1 truncate font-serif text-2xl font-semibold text-[var(--ap-ink)]">
                    {vendor?.business_name || "Your kitchen"}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#ded6cb] bg-white"
                aria-label="Close kitchen menu"
              >
                <X size={19} aria-hidden="true" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-4">
              {arranging && (
                <div className="mb-4 rounded-xl border border-[#e5b995] bg-white p-3" data-testid="seller-menu-arrange-panel">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-extrabold text-[var(--ap-ink)]">Arrange optional tools</div>
                      <p className="mt-0.5 text-[10px] leading-4 text-[var(--ap-ink-soft)]">Choose where each tool lives. Storefront, Orders, and Menu stay fixed.</p>
                    </div>
                    <button type="button" onClick={() => setArranging(false)} className="min-h-9 rounded-full bg-[var(--ap-ink)] px-3 text-[10px] font-bold text-white">Done</button>
                  </div>
                  <button type="button" onClick={resetMenu} className="mt-2 text-[10px] font-semibold text-[var(--ap-ink-soft)] underline underline-offset-2">Reset optional tools to More</button>
                </div>
              )}

              <MenuGroup
                label="Workspace"
                items={workspaceItems}
                arranging={arranging}
                onLongPressStart={beginLongPress}
                onLongPressEnd={endLongPress}
                suppressNextClick={suppressNextClick}
                placements={preferences.placements}
                onPlace={placeTool}
                onMove={moveTool}
              />

              {vendor?.status === "approved" && (
                <MoreToolsMenu
                  items={moreItems}
                  arranging={arranging}
                  onLongPressStart={beginLongPress}
                  onLongPressEnd={endLongPress}
                  suppressNextClick={suppressNextClick}
                  placements={preferences.placements}
                  onPlace={placeTool}
                  onMove={moveTool}
                />
              )}

              <MenuGroup label="Account" items={ACCOUNT} />

              {showSetup && (
                <MenuGroup
                  label="Setup"
                  items={[{
                    to: "/vendor/setup",
                    label: "Application",
                    icon: ListChecks,
                    testid: "seller-menu-setup",
                  }]}
                />
              )}
            </nav>

            <div className="border-t border-[#eadfd3] p-4">
              {!arranging && vendor?.status === "approved" && (
                <p className="mb-2 text-center text-[10px] text-[var(--ap-ink-soft)]">
                  Long-press Prep Board, Fulfillment, Growth, or Preorder Drops to arrange the menu.
                </p>
              )}
              {vendor?.id && vendor.status === "approved" && vendor.is_public && (
                <Link
                  to={`/vendor/${vendor.id}`}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#ded6cb] bg-white px-4 text-sm font-semibold text-[var(--ap-ink)]"
                  data-testid="seller-menu-view-storefront"
                >
                  <ExternalLink size={16} aria-hidden="true" />
                  View live storefront
                </Link>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

function MoreToolsMenu(props) {
  const { items, arranging } = props;
  if (arranging) {
    return (
      <section className="mb-5" data-testid="seller-menu-more">
        <div className="px-3 pb-1.5 text-[9px] font-bold uppercase tracking-[0.28em] text-[#526174]">More</div>
        <div className="ml-3 space-y-1 border-l border-[#ded6cb] pl-3">
          {items.length ? items.map((item) => <MenuItem key={item.to} item={item} {...props} />) : (
            <div className="px-3 py-2 text-[11px] text-[var(--ap-ink-soft)]">All optional tools are pinned above.</div>
          )}
        </div>
      </section>
    );
  }

  return (
    <details className="mb-5" data-testid="seller-menu-more">
      <summary className="min-h-11 cursor-pointer list-none rounded-xl px-3.5 py-3 text-sm font-semibold text-[var(--ap-ink)] hover:bg-white">
        More
      </summary>
      <div className="ml-3 mt-1 space-y-1 border-l border-[#ded6cb] pl-3">
        {items.map((item) => <MenuItem key={item.to} item={item} {...props} />)}
      </div>
    </details>
  );
}

function MenuGroup({
  label,
  items,
  arranging = false,
  onLongPressStart,
  onLongPressEnd,
  suppressNextClick,
  placements,
  onPlace,
  onMove,
}) {
  return (
    <section className="mb-5">
      <div className="px-3 pb-1.5 text-[9px] font-bold uppercase tracking-[0.28em] text-[#526174]">
        {label}
      </div>
      <div className="space-y-1">
        {items.map((item) => (
          <MenuItem
            key={item.to}
            item={item}
            arranging={arranging}
            onLongPressStart={onLongPressStart}
            onLongPressEnd={onLongPressEnd}
            suppressNextClick={suppressNextClick}
            placements={placements}
            onPlace={onPlace}
            onMove={onMove}
          />
        ))}
      </div>
    </section>
  );
}

function MenuItem({
  item,
  arranging = false,
  onLongPressStart,
  onLongPressEnd,
  suppressNextClick,
  placements,
  onPlace,
  onMove,
}) {
  const { to, label, icon: Icon, testid, subItem, customizable } = item;

  if (customizable && arranging) {
    const placement = placements?.[item.id] || "more";
    return (
      <div className={`rounded-xl border border-[#eadfd3] bg-white p-2.5 ${subItem ? "ml-5" : ""}`} data-testid={`${testid}-arrange`}>
        <div className="flex items-center gap-2">
          <GripVertical size={15} className="text-[#8a8178]" aria-hidden="true" />
          <Icon size={17} strokeWidth={1.9} aria-hidden="true" />
          <span className="min-w-0 flex-1 text-sm font-semibold text-[var(--ap-ink)]">{label}</span>
          <button type="button" onClick={() => onMove?.(item.id, -1)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#e5dacd]" aria-label={`Move ${label} up`}><ChevronUp size={15} /></button>
          <button type="button" onClick={() => onMove?.(item.id, 1)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#e5dacd]" aria-label={`Move ${label} down`}><ChevronDown size={15} /></button>
        </div>
        <div className="mt-2 grid grid-cols-4 gap-1">
          {[
            ["storefront", "Storefront"],
            ["orders", "Orders"],
            ["menu", "Menu"],
            ["more", "More"],
          ].map(([value, text]) => (
            <button
              key={value}
              type="button"
              onClick={() => onPlace?.(item.id, value)}
              className={`min-h-8 rounded-lg px-1 text-[9px] font-bold ${placement === value ? "bg-[var(--ap-primary)] text-white" : "border border-[#eadfd3] text-[var(--ap-ink-soft)]"}`}
              aria-pressed={placement === value}
            >
              {text}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      end={to === "/vendor/storefront"}
      data-testid={testid}
      onPointerDown={customizable ? onLongPressStart : undefined}
      onPointerUp={customizable ? onLongPressEnd : undefined}
      onPointerCancel={customizable ? onLongPressEnd : undefined}
      onPointerLeave={customizable ? onLongPressEnd : undefined}
      onContextMenu={customizable ? (event) => {
        event.preventDefault();
        onLongPressEnd?.();
      } : undefined}
      onClick={customizable ? (event) => {
        if (suppressNextClick?.current) {
          event.preventDefault();
          suppressNextClick.current = false;
        }
      } : undefined}
      className={({ isActive }) =>
        `flex min-h-12 items-center gap-3 rounded-xl text-sm transition-colors ${subItem ? "ml-6 border-l border-[#ded6cb] pl-3 pr-2 text-xs " : "px-3.5 "}${
          isActive
            ? "bg-[var(--ap-primary)] font-semibold text-white"
            : "text-[var(--ap-ink)] hover:bg-white"
        }`
      }
    >
      <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
      <span className="flex-1">{label}</span>
      {customizable && <span className="sr-only">Long press to move this menu item.</span>}
    </NavLink>
  );
}
